import ChevronRightIcon from 'components/atoms/icons/chevron-right';
import { useTranslation } from 'react-i18next';
import { DateFormatter, prepareDoctorName } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';

import s from './styles.module.scss';

import type { TCombinedStatusLog } from 'infrastructure/hooks/logs/use-combined-status-logs';
import type { Nullable } from 'infrastructure/types';
import type { IBaseTableColumns } from 'components/atoms/base-table/types';

export const logsColumns = (): IBaseTableColumns<TCombinedStatusLog> => {
  const { t } = useTranslation();

  const checkValue = (value?: Nullable<string>) =>
    !value || value === 'null' ? t('labels.bannerNoData') : value;

  const getFrom = (data: TCombinedStatusLog): string | undefined => {
    if (data.logType === 'rpm') {
      return data.oldValue
        ? t(`rpmStatuses.${data.oldValue}` as any)
        : undefined;
    }

    return data.oldValue ? t(`ccmStatuses.${data.oldValue}`) : undefined;
  };

  const getTo = (data: TCombinedStatusLog): string | undefined => {
    if (data.logType === 'rpm') {
      return data.newValue
        ? t(`rpmStatuses.${data.newValue}` as any)
        : undefined;
    }
    return data.newValue ? t(`ccmStatuses.${data.newValue}`) : undefined;
  };

  return [
    {
      label: t('labels.dateTime'),
      key: 'createdAt',
      width: '170px',
      render(value) {
        const date = DateFormatter({
          date: value,
          format: DateFormats['MM-dd-yyyy | h:mm a'],
        });
        return (
          <span className={s.date} data-cy="logs-dateChanged">
            {date}
          </span>
        );
      },
    },
    {
      label: t('labels.logType'),
      key: 'logType',
      width: '80px',
      align: 'center',
      render(value) {
        return (
          <span className={s.date} data-cy="logs-status-changed-type">
            {value.toUpperCase()}
          </span>
        );
      },
    },
    {
      label: t('labels.statusChanging'),
      key: 'from',
      width: '400px',
      responsive: ['md'],
      render(_, record) {
        const from = getFrom(record);
        const to = getTo(record);

        return (
          <div data-cy="logs-status-change">
            {from && (
              <>
                <span className={s.from} data-cy="logs-status-change-from">
                  {`${t('labels.from')}: ${checkValue(from)} `}
                </span>
                <ChevronRightIcon />
              </>
            )}
            <span className={s.to} data-cy="logs-status-change-to">
              {` ${t('labels.to')}: ${checkValue(to)}`}
            </span>
          </div>
        );
      },
    },
    {
      label: t('labels.electronicallySigned'),
      align: 'right',
      key: 'who',
      width: '330px',
      render(_, record) {
        const who = prepareDoctorName(record.updatedByLogin);
        return (
          <span className={s.who} data-cy="logs-electronicallySigned">
            {who}
          </span>
        );
      },
    },
  ];
};
